import {
  ALGOLIA_PRODUCT_SORT_BY,
  CART_MAX_QUANTITY,
  CHANNELS,
  PRODUCT_BUNDLE_RATE,
} from '../constants/algolia';
import { Channel, COLLECTION_NOVELTIES } from '../constants';

import { ISearchProduct, IUserData } from '@monofront/types';
import qs from 'qs';
import { format } from './price';
import connectAutocomplete, {
  AutocompleteConnectorParams,
  AutocompleteWidgetDescription,
} from 'instantsearch.js/es/connectors/autocomplete/connectAutocomplete';
import { useConnector } from 'react-instantsearch-core';
import { IndexUiState } from 'instantsearch.js/es/types';
import { logger } from '@monofront/logging';

type IBundleProductDetails =
  | {
      productBundleRate: string;
      productBundleNumber: number;
      productBundleSale: number;
    }
  | {
      productBundleRate: null;
      productBundleNumber: null;
      productBundleSale: null;
    };

export const getBundleProductDetails = (
  tags: string[],
): IBundleProductDetails => {
  const [productBundleRate] = tags.filter((tag) =>
    tag.startsWith(PRODUCT_BUNDLE_RATE.STARTWITH),
  );

  if (!productBundleRate) {
    return {
      productBundleRate: null,
      productBundleNumber: null,
      productBundleSale: null,
    };
  }

  const [productBundleNumber, productBundleSale] =
    productBundleRate.match(/\d+/g) || [];

  return {
    productBundleRate,
    productBundleNumber: Number(productBundleNumber),
    productBundleSale: Number(productBundleSale),
  };
};

export const getMaxQuantity = ({
  tags,
  inventory_quantity,
}: ISearchProduct): number => {
  const maxQuantity = Number(
    tags
      .filter((tag) => tag.startsWith(CART_MAX_QUANTITY.STARTWITH))[0]
      ?.replace(CART_MAX_QUANTITY.STARTWITH, '') || '0',
  );

  if (!maxQuantity) {
    return inventory_quantity;
  }

  return Math.min(maxQuantity, inventory_quantity);
};

export const getMembershipFormattedPrice = (product: ISearchProduct) =>
  product.meta.finance?.unit_price
    ? format(product.meta.finance?.unit_price)
    : undefined;

export const buildDefaultSortBy = (
  collectionHandle?: string | null,
): string => {
  // TODO: find a way to configure that elsewhere
  if (collectionHandle === COLLECTION_NOVELTIES) {
    return ALGOLIA_PRODUCT_SORT_BY.DATE;
  }

  return ALGOLIA_PRODUCT_SORT_BY.POPULARITY;
};

export const createURL = (state) => {
  return `?${qs.stringify(state, { format: 'RFC1738' })}`;
};

export const getUserData = (
  userData: IUserData[] | undefined,
  dataType: string,
) => {
  return userData?.find((item: any) => item.type === dataType)?.data;
};

export const generateBaseProductIndexName = ({
  environment,
  app,
  channel,
}: {
  environment: 'production' | 'preprod';
  app: boolean;
  channel: Channel;
}) => {
  const parts: Array<string> = [environment];

  if (app) {
    parts.push('app');
  }

  parts.push('products');

  switch (channel) {
    case Channel.FR:
      parts.push(CHANNELS.FR);
      break;
    case Channel.DE:
      parts.push(CHANNELS.DE);
      break;
    default:
      console.error(
        `generateProductIndexName: Channel ${channel} not found. Default to ${parts.join(
          '_',
        )}.`,
      );
  }

  return parts.join('_');
};

export const generateProductIndexNameWithBaseName = ({
  baseIndexName,
  search = false,
  sort = ALGOLIA_PRODUCT_SORT_BY.POPULARITY,
  oos,
}: {
  baseIndexName: string;
  search?: boolean;
  sort?: string;
  oos?: boolean;
}) => {
  const parts = [baseIndexName];

  if (search) {
    parts.push('search');
  }

  if (sort) {
    parts.push(sort);
  }

  if (oos) {
    parts.push('oos');
  }

  return parts.join('_');
};

export const generateProductIndexName = (
  environment = 'production',
  app = false,
  channel: Channel = Channel.FR,
  search = false,
  sort?: string,
  oos?: boolean,
): string => {
  const parts = [environment];

  if (app) {
    parts.push('app');
  }

  parts.push('products');

  switch (channel) {
    case Channel.FR:
      parts.push(CHANNELS.FR);
      break;
    case Channel.DE:
      parts.push(CHANNELS.DE);
      break;
    default:
      console.error(
        `generateProductIndexName: Channel ${channel} not found. Default to ${parts.join(
          '_',
        )}.`,
      );
  }

  if (search) {
    parts.push('search');
  }

  if (sort) {
    parts.push(sort);
  }

  if (oos) {
    parts.push('oos');
  }

  return parts.join('_');
};

export const generateCollectionIndexName = (
  environment = 'production',
  locale = 'fr',
): string => {
  const parts = [environment, 'collections'];

  switch (locale) {
    case 'fr':
      parts.push('fr_FR');
      break;
    case 'de':
      parts.push('de_DE');
      break;
    default:
      throw new Error('Locale not found');
  }

  return parts.join('_');
};

export const getRootPath = (channel: Channel = Channel.FR): string => {
  switch (channel) {
    case Channel.FR:
      return 'all|Notre sélection';
    case Channel.DE:
      return 'unsere-auswahl-de|Unsere Auswahl';
    default:
      throw new Error('Channel not found for Root Path');
  }
};

export type UseAutocompleteProps = AutocompleteConnectorParams;
export function useAutocomplete(props?: UseAutocompleteProps) {
  return useConnector<
    AutocompleteConnectorParams,
    AutocompleteWidgetDescription
  >(connectAutocomplete, props);
}

export const urlToSearchState =
  (indexName: string) =>
  ({
    qsModule,
    location,
  }: {
    qsModule: typeof qs;
    location: {
      href: string;
      search: string;
      pathname: string;
      protocol: string;
      host: string;
    };
  }) => {
    const queryString = location.href.includes('?')
      ? qsModule.parse(location.search.slice(1))
      : {};

    // Algolia next's router doesn't handle dynamic route params, and sometimes it
    // is available in searchParams (when navigating from one page to another, initial props
    // are fetched from `/_next/data/*/fr/collections/<collectionHandle>`), most of the time we
    // need to parse the pathname ourselves...
    // The old way was to pass an initial state to the index, but `initialUiState`
    // is ignored when we use a router in InstantSearch (needed for SSR).
    // ⚠️ Not handling search param will make a search with no collection filter on page=1!
    const maybeSearchParamCollectionHandle = queryString.collectionHandle as
      | string
      | undefined;

    // `/collections/[collectionHandle]`
    // [0]: leading slash; [1] collections,products,pages,...; [2]: collectionHandle,productHandle
    const [, , maybePathCollectionHandle] = location.pathname.split('/');

    // Both qs and pathname split don't type undefined, but handle can be missing in both...
    const collectionHandle =
      maybeSearchParamCollectionHandle ??
      (maybePathCollectionHandle as string | undefined);

    const {
      categories,
      shelfs,
      values,
      diets,
      vendor,
      'meta.marketing.nutriscore_grade': nutriscoreMark,
      'meta.marketing.environmentalscore_grade': environmentalscoreMark,
      page,
      query,
      sortBy,
      menu,
    } = queryString;

    const newState: IndexUiState = {
      ...((categories ||
        shelfs ||
        diets ||
        values ||
        nutriscoreMark ||
        environmentalscoreMark ||
        vendor ||
        collectionHandle) && {
        refinementList: {
          ...(categories &&
            typeof categories === 'string' && {
              categories: categories.split(','),
            }),
          ...(shelfs &&
            typeof shelfs === 'string' && { shelfs: shelfs.split(',') }),
          ...(diets &&
            typeof diets === 'string' && { diets: diets.split(',') }),
          ...(values &&
            typeof values === 'string' && { values: values.split(',') }),
          ...(vendor &&
            typeof vendor === 'string' && { vendor: vendor.split(',') }),
          ...(nutriscoreMark &&
            typeof nutriscoreMark === 'string' && {
              'meta.marketing.nutriscore_grade': nutriscoreMark.split(','),
            }),
          ...(environmentalscoreMark &&
            typeof environmentalscoreMark === 'string' && {
              'meta.marketing.environmentalscore_grade':
                environmentalscoreMark.split(','),
            }),
          ...(collectionHandle && {
            collections: [collectionHandle],
          }),
        },
      }),
      sortBy: generateProductIndexNameWithBaseName({
        baseIndexName: indexName,
        search: !collectionHandle,
        sort:
          typeof sortBy === 'string'
            ? sortBy
            : buildDefaultSortBy(collectionHandle),
      }),
      page: typeof page === 'string' ? parseInt(page, 10) : 1,
      ...(query && typeof query === 'string' && { query: query }),
      ...(menu &&
        typeof menu === 'object' &&
        !Array.isArray(menu) && {
          menu: Object.fromEntries(
            Object.entries(menu).map(([key, value]) => [key, String(value)]),
          ),
        }),
    };

    return { [indexName]: newState };
  };

export const searchStateToURL =
  (indexName: string) =>
  ({
    qsModule,
    location,
    routeState,
  }: {
    qsModule: typeof qs;
    location: {
      href: string;
      search: string;
      pathname: string;
      protocol: string;
      host: string;
    };
    routeState: IndexUiState;
  }): string => {
    const queryString = location.href.includes('?')
      ? qsModule.parse(location.search.slice(1))
      : {};

    const {
      categorie: _categorie,
      values: _values,
      diets: _diets,
      shelfs: _shelfs,
      vendor: _vendor,
      'meta.marketing.nutriscore_grade': _nutriscore,
      'meta.marketing.environmentalscore_grade': _environmentalscore,
      sortBy: _sortBy,
      page: _page,
      // Extract params unrelated to Algolia to preserve them in the URL
      // (trackingId, shared wishlists...)
      ...otherParams
    } = queryString;

    const baseUrl = `${location.protocol}//${location.host}${location.pathname}`;

    const indexState = routeState[indexName];

    const categories: string | false | undefined =
      Boolean(indexState?.refinementList?.categories) &&
      indexState.refinementList?.categories?.join(',');

    const shelfs: string | false | undefined =
      Boolean(indexState?.refinementList?.shelfs) &&
      indexState.refinementList?.shelfs?.join(',');

    const diets: string | false | undefined =
      Boolean(indexState?.refinementList?.diets) &&
      indexState?.refinementList?.diets?.join(',');

    const values: string | false | undefined =
      Boolean(indexState?.refinementList?.values) &&
      indexState.refinementList?.values?.join(',');

    const vendor: string | false | undefined =
      Boolean(indexState?.refinementList?.vendor) &&
      indexState.refinementList?.vendor?.join(',');

    const nutriscoreMark: string | false | undefined =
      Boolean(
        indexState?.refinementList?.['meta.marketing.nutriscore_grade'],
      ) &&
      indexState.refinementList?.['meta.marketing.nutriscore_grade']?.join(',');

    const environmentalscoreMark: string | false | undefined =
      Boolean(
        indexState?.refinementList?.['meta.marketing.environmentalscore_grade'],
      ) &&
      indexState.refinementList?.[
        'meta.marketing.environmentalscore_grade'
      ]?.join(',');

    const {
      configure: _configure,
      refinementList,
      range: _range,
      sortBy,
      page,
      ...filteredSearchState
    } = indexState;

    // SortBy widget value is the complete index, we extract only the important information from it
    const sort = sortBy ? getAlgoliaSortByFromIndexName(sortBy) : null;

    const newSearchState = {
      ...(categories && { categories }),
      ...(values && { values }),
      ...(diets && { diets }),
      ...(shelfs && { shelfs }),
      ...(vendor && { vendor }),
      ...(nutriscoreMark && {
        'meta.marketing.nutriscore_grade': nutriscoreMark,
      }),
      ...(environmentalscoreMark && {
        'meta.marketing.environmentalscore_grade': environmentalscoreMark,
      }),
      // Only store sortBy in the URL if it isn't popularity (default)
      ...(sort &&
        sort !== ALGOLIA_PRODUCT_SORT_BY.POPULARITY && {
          sortBy: sort,
        }),
      ...(page &&
        page > 1 && {
          page,
        }),
      ...filteredSearchState,
      ...otherParams,
    };

    if (indexState?.hierarchicalMenu?.['hierarchicalCategories.lvl0'] === '') {
      return baseUrl;
    }

    if (indexState?.query && !refinementList?.collections?.[0]) {
      // -- We are in a SEARCH context --

      return `${baseUrl}?${qsModule.stringify(newSearchState, {
        format: 'RFC1738',
      })}`;
    }

    // During SSR hydration, the routing middleware fires before refinement list widgets
    // have registered via useLayoutEffect. In that case refinementList is undefined and
    // building a URL from the empty state would strip all params from the address bar.
    // Returning the current URL here makes shouldWrite see no change → no write → params preserved.
    if (refinementList === undefined) {
      return `${location.protocol}//${location.host}${location.pathname}${location.search}`;
    }

    // -- We are in a COLLECTION context --
    const hierarchicalHandle = refinementList?.collections[0];

    const pathName = hierarchicalHandle
      ? `/collections/${hierarchicalHandle}`
      : location.pathname;

    if (
      hierarchicalHandle === COLLECTION_NOVELTIES &&
      newSearchState.sortBy === ALGOLIA_PRODUCT_SORT_BY.DATE
    ) {
      delete newSearchState.sortBy;
    }

    const url: string = Object.keys(newSearchState).length
      ? `${pathName}?${qsModule.stringify(newSearchState, {
          format: 'RFC1738',
        })}`
      : pathName;

    return `${location.protocol}//${location.host}${url}`;
  };

const getAlgoliaSortByFromIndexName = (indexName: string) => {
  for (const [, value] of Object.entries(ALGOLIA_PRODUCT_SORT_BY)) {
    if (indexName.includes(value)) {
      return value;
    }
  }

  logger.warn('Unexpected algolia index name', { indexName });

  return null;
};
